import { useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Wrench } from "lucide-react";

import { InternalService } from "@/generated";
import { TOOL_ICON_BY_NAME } from "@/components/NewAndIncredibleChatWidget/toolDisplay";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { SearchBar } from "@/components/SearchBar";
import { Switch } from "@/components/shadcn/switch";
import { Skeleton } from "@/components/shadcn/skeleton";
import { Banner } from "@/components/Banner";
import { cn } from "@/lib/utils";

export const ToolSelect = ({
  value,
  onChange,
  label,
  tooltip,
  isDisabled,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  label?: string | null;
  tooltip?: string | null;
  isDisabled?: boolean;
}) => {
  const [search, setSearch] = useState("");

  const {
    data: toolsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["tool-displays"],
    queryFn: () => InternalService.getToolDisplays(),
    staleTime: 60_000,
  });
  const tools = toolsData?.data;

  const visibleTools = useMemo(() => {
    if (!tools) return [];
    const query = search.trim().toLowerCase();
    if (!query) return tools;
    return tools.filter(
      (tool) =>
        tool.label.toLowerCase().includes(query) ||
        tool.name.toLowerCase().includes(query)
    );
  }, [tools, search]);

  const toggle = (toolName: string, hidden: boolean) => {
    onChange(
      hidden ? [...value, toolName] : value.filter((n) => n !== toolName)
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-5.5 w-[40%]" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (isError || !tools) {
    return (
      <Banner
        style="error"
        label="There was an issue fetching the tool list. Please refresh the page and try again."
      />
    );
  }

  return (
    <div className="flex flex-col">
      {label && <NoxusFormLabel label={label} tooltip={tooltip ?? undefined} />}
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search tools"
      />
      <div className="flex flex-col max-h-96 mt-4 overflow-y-auto rounded-md border border-border divide-y divide-border">
        {visibleTools.map((tool) => {
          const Icon = TOOL_ICON_BY_NAME[tool.icon] ?? Wrench;
          const hidden = value.includes(tool.name);
          return (
            // The row must stay a <label>: inside a <form>, Radix Switch
            // re-dispatches a bubbling click from its hidden input whenever
            // `checked` changes, so a row onClick would re-toggle forever.
            // Labels ignore activation from interactive descendants.
            <label
              key={tool.name}
              className={cn(
                "flex items-center gap-2 px-3 py-2 transition-colors",
                isDisabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:bg-accent/50"
              )}
            >
              <Icon className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-sm text-primary truncate">
                {tool.label}
              </span>
              <Switch
                checked={hidden}
                onCheckedChange={(checked) => toggle(tool.name, checked)}
                disabled={isDisabled}
                className="ml-auto"
              />
            </label>
          );
        })}
        {visibleTools.length === 0 && (
          <p className="px-3 py-4 text-sm text-muted-foreground">
            No tools match your search.
          </p>
        )}
      </div>
    </div>
  );
};

export const ToolSelectField = ({
  name,
  control,
  label,
  tooltip,
  isDisabled,
}: {
  name: string;
  control?: Control<any>;
  label?: string | null;
  tooltip?: string | null;
  isDisabled?: boolean;
}) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <ToolSelect
          value={field.value ?? []}
          onChange={field.onChange}
          label={label}
          tooltip={tooltip}
          isDisabled={isDisabled}
        />
      )}
    />
  );
};
